import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Popover } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  AssignmentPlan,
  AssignmentSnapshot,
  canClearEmployee,
  employeeInSlot,
  PlannerSlot,
  slotDescription,
} from "@/components/appointmentRequest/assignmentPlanner/assignmentPlan";
import { filterPlannerEmployees } from "@/components/appointmentRequest/assignmentPlanner/employeeFiltering";
import PlannerEmployeeList from "@/components/appointmentRequest/assignmentPlanner/plannerEmployeeList";
import PlannerEmployeeSearch from "@/components/appointmentRequest/assignmentPlanner/plannerEmployeeSearch";
import PlannerToggle from "@/components/appointmentRequest/assignmentPlanner/plannerToggle";
import styles from "@/components/appointmentRequest/assignmentPlanner/planner.module.css";

interface Props {
  open?: boolean;
  anchor?: HTMLElement | null;
  slot?: PlannerSlot;
  snapshot: AssignmentSnapshot;
  plan: AssignmentPlan;
  onChoose: (id: string) => void;
  onClose: () => void;
}
export default function PlannerSlotPopover({
  open = true,
  anchor,
  slot,
  snapshot,
  plan,
  onChoose,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [includeAssigned, setIncludeAssigned] = useState(false);
  const [departments, setDepartments] = useState<string[]>([]);
  const searchInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!open) {
      setSearch("");
      setIncludeAssigned(false);
      setDepartments([]);
    }
  }, [open]);
  useLayoutEffect(() => {
    if (open)
      queueMicrotask(() => searchInput.current?.focus({ preventScroll: true })); // the portal can mount after this layout effect.
  }, [open]);
  const currentId = slot ? employeeInSlot(plan, slot.id) : undefined;
  const current = snapshot.employees.find(
    (employee) => employee.id === currentId,
  );
  const options = useMemo(
    () =>
      filterPlannerEmployees(snapshot.employees, search, departments).filter(
        (employee) => includeAssigned || !plan[employee.id],
      ),
    [snapshot.employees, search, departments, includeAssigned, plan],
  );
  const toggle = useMemo(
    () => <PlannerToggle all={includeAssigned} onChange={setIncludeAssigned} />,
    [includeAssigned],
  );
  return (
    <Popover
      open={open}
      keepMounted
      transitionDuration={0}
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
    >
      <div
        className={styles.popover}
        role="dialog"
        aria-label={t("planner.assign")}
      >
        <strong>{slotDescription(slot)}</strong>
        {current && (
          <small>
            {t("planner.current")}: {current.name}
          </small>
        )}
        <PlannerEmployeeSearch
          employees={snapshot.employees}
          search={search}
          onSearch={setSearch}
          departments={departments}
          onDepartments={setDepartments}
          inputRef={searchInput}
          toggle={toggle}
        />
        <PlannerEmployeeList
          snapshot={snapshot}
          plan={plan}
          employees={options}
          className={styles.pickerEmployeeList}
          isDisabled={(employee) =>
            employee.id === currentId ||
            Boolean(current && !canClearEmployee(current))
          }
          onChoose={(employee) => onChoose(employee.id)}
          emptyMessage={t("planner.no_matching_employees")}
        />
      </div>
    </Popover>
  );
}
